<template>
  <loading-wrapper :is-loading="!contentTypeMapping">
    <div class="mass-upload-container">
      <div class="top-panel">
        <h3>{{ $t('documentUpload.massUpload') }} {{ referenceNumber }}</h3>
        <div v-if="isDigitalizationProjectMode()">
          {{ $t('documentUpload.disabledCheckBotInspection') }}:
          {{ $t(`generic.${isCheckBotInspectionDisabled ? 'yes' : 'no'}`) }}
        </div>
        <div v-if="isDigitalizationProjectMode()">
          {{ $t('documentUpload.noMetadataUpload') }}:
          {{ $t(`generic.${isNoMetadataUploadMode ? 'yes' : 'no'}`) }}
        </div>
      </div>

      <main>
        <upload-frames
          :key="uploadFramesRerenderKey"
          :are-no-metadata-file-inputs-disabled="!!uploadRecords.length"
          :is-csv-input-disabled="!!uploadRecords.length"
          :is-no-metadata-upload-mode="isNoMetadataUploadMode"
          :upload-progress="isUploading ? uploadProgress : undefined"
          @reading-zip-file="handleReadingZipFile"
          @zip-reading-error="handleZipReadingError"
          @mass-upload-records="addMassUploadRecords"
          @mass-upload-files="addMassUploadFiles"
        />

        <div class="zip-tool-hint">{{ $t('documentUpload.zipToolHint') }}</div>

        <message-wrapper v-if="isWrongCsvError" severity="error">
          {{ $t('documentUpload.columnsWithMissingContent') }}
          {{ getListOfMissingFields() }}
        </message-wrapper>
        <message-wrapper v-if="isDocIdsWithToManyFilesError" severity="error">
          {{
            t('documentUpload.errors.toManyFilesForDocId', {
              documentIds: Array.from(docIdsWithToManyFiles).join(', '),
            })
          }}
        </message-wrapper>
        <message-wrapper v-if="isMissingMainFileError" severity="error">
          {{
            t('documentUpload.errors.mainFileMissing', {
              documentIds: Array.from(docIdsWithMissingMainFile).join(', '),
            })
          }}
        </message-wrapper>
        <message-wrapper v-if="isMissingRenditionRecordError" severity="error">
          {{
            t('documentUpload.errors.renditionRecordMissing', {
              documentIds: recordsWithMissingReditionFile.join(', '),
            })
          }}
        </message-wrapper>

        <mass-upload-table
          v-if="displayUploadTable"
          :file-upload-statuses="fileUploadStatuses"
          :metadata-changes="metadataChanges"
          :supplier-id="supplierId!"
          :table-busy="isUploadTableBusy"
          :upload-records="uploadRecords"
          @updated-records="handleUpdateRecords"
        />

        <vf-button
          v-if="displayUploadTable"
          :disabled="isUploading || isReadingZipFile"
          :size="buttonSizes.large"
          :variant="buttonVariants.primary"
          @click="checkMissingFilesAndUpload"
        >
          {{ $t('documentUpload.uploadFiles') }}
        </vf-button>
      </main>

      <metadata-validation-modal
        ref="metadataValidationModalRef"
        :invalid-document-ids="invalidDocumentIds"
      ></metadata-validation-modal>
      <missing-main-files-modal
        ref="missingMainFilesModalRef"
        :records-without-main-files="Array.from(docIdsWithOnlyRenditionFile)"
      >
      </missing-main-files-modal>
      <missing-files-modal
        v-if="recordsWithoutFile"
        ref="missingFilesModalRef"
        :records-without-file="recordsWithoutFile"
        @ignore-missing-files="handleIgnoreMissingFiles"
      />
    </div>
  </loading-wrapper>
</template>
<script setup lang="ts">
  import { UploadType } from '@/components/MassUploadTable/MassUploadTable.types';
  import MetadataValidationModal from '@/components/MetadataValidationModal/MetadataValidationModal.vue';
  import { useGetContentTypeMapping } from '@/composables/api/content-types/use-get-content-type-mapping';
  import { useNavigator } from '@/composables/useNavigator';
  import { useToast } from '@/composables/useToast';
  import { DigitalizationProjectDetailsTab } from '@/views/DigitalizationProjectDetails/DigitalizationProjectDetails.types';
  import { ProjectDetailsTab } from '@/views/ProjectDetails/ProjectDetails.types';
  import { ProjectType } from 'types';
  import { computed, onMounted, ref, toRef, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import MissingFilesModal from '../../components/MissingFilesModal/MissingFilesModal.vue';
  import UploadFrames from '../../components/UploadFrames/UploadFrames.vue';
  import {
    useFilesMapping,
    useMassUpload,
    useMassUploadRecordsHandler,
    useMissingFilesChecker,
    useVersionsDiff,
  } from './composables';
  import { IMassUploadFile, IUploadRecord } from './MassUpload.types';

  const { ...props } = defineProps<{
    isCheckBotInspectionDisabled?: boolean;
    isNoMetadataUploadMode?: boolean;
    mode: ProjectType;
    referenceNumber: string;
    contentTypeId: string;
    checkBotConfigId?: string;
    projectId: string;
    supplierId?: string;
  }>();
  /**
   * These props need to be converted to refs as they are used in a composable.
   * Initially they are undefined and change later to values extracted from the
   * route query.
   */
  const isNoMetadataUploadMode = toRef(props, 'isNoMetadataUploadMode', false);
  const isCheckBotInspectionDisabled = toRef(
    props,
    'isCheckBotInspectionDisabled',
    false,
  );

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const { goToProjectDetailsTab } = useNavigator();

  const uploadRecords = ref<IUploadRecord[]>([]);
  const massUploadFiles = ref<IMassUploadFile[]>([]);
  const isUploadConfirmed = ref(false);
  const isUploading = ref(false);
  const isUploadFinished = ref(false);
  const uploadFramesRerenderKey = ref(1);
  const isUploadFailed = ref(false);
  const isReadingZipFile = ref(false);
  const skippedFiles = ref<string[]>([]);

  const { contentTypeMapping, getContentTypeMapping } =
    useGetContentTypeMapping(
      props.mode,
      props.contentTypeId,
      props.projectId,
      props.supplierId,
    );
  const {
    addMassUploadRecords,
    isWrongCsvError,
    columnsWithMissingValues,
    isDocIdsWithToManyFilesError,
    docIdsWithToManyFiles,
    isMissingMainFileError,
    docIdsWithMissingMainFile,
    missingMainFilesModalRef,
    docIdsWithOnlyRenditionFile,
    invalidDocumentIds,
    metadataValidationModalRef,
  } = useMassUploadRecordsHandler(
    isNoMetadataUploadMode,
    uploadRecords,
    contentTypeMapping,
  );
  const {
    isGettingDiff,
    metadataChanges,
    hasRenditionFileByDocumentId,
    isMissingRenditionRecordError,
    recordsWithMissingReditionFile,
  } = useVersionsDiff(props.projectId, props.supplierId, uploadRecords);
  useFilesMapping(massUploadFiles, uploadRecords, isReadingZipFile);
  const { fileUploadStatuses, uploadProgress, uploadFilesAndMetadata } =
    useMassUpload(
      uploadRecords,
      props.projectId,
      props.supplierId,
      props.referenceNumber,
      props.contentTypeId,
      props.checkBotConfigId,
      isCheckBotInspectionDisabled,
      skippedFiles,
    );
  const {
    recordsWithoutFile,
    areRecordsWithoutFilePresent,
    isMetadataChanged,
    getFilePathsOfRecordsWithMissingFiles,
    removeInvalidRecords,
  } = useMissingFilesChecker(uploadRecords, hasRenditionFileByDocumentId);

  watch(uploadRecords, assignUploadTypes, { deep: true });

  const displayUploadTable = computed<boolean>(
    () =>
      !!uploadRecords.value.length &&
      !isWrongCsvError.value &&
      !isDocIdsWithToManyFilesError.value &&
      !isMissingMainFileError.value &&
      !isMissingRenditionRecordError.value,
  );

  const isUploadTableBusy = computed(
    () => isGettingDiff.value || isReadingZipFile.value,
  );

  function assignUploadTypes() {
    uploadRecords.value.forEach((record) => {
      if (!record.hasPreviousVersion) return;
      const isNotEmptyFile = !!record.file?.size;
      const isRenditionRecord = record.renditionFile;
      const isNotEmptyRenditionFile = !!record.renditionFile?.file?.size;
      /**
       * If a rendition file is expected, both files (PDF + rendition) must be
       * present in order to treat the row as a file update.
       */
      const isValidFileCombination =
        isNotEmptyFile && (!isRenditionRecord || isNotEmptyRenditionFile);
      if (isMetadataChanged(record)) {
        record.uploadType = UploadType.UPDATED_METADATA;
      } else if (record.hasPreviousVersion && isValidFileCombination) {
        record.uploadType = UploadType.UPDATED_DOCUMENT;
      }
    });
  }

  function handleUpdateRecords(updatedRecords: IUploadRecord[]) {
    // BUG: File name is not updated when file is exchanged after adding ZIP
    uploadRecords.value = updatedRecords;
  }

  function handleReadingZipFile() {
    isReadingZipFile.value = true;
  }
  function handleZipReadingError() {
    isReadingZipFile.value = false;
  }

  function getListOfMissingFields(): string {
    return Array.from(columnsWithMissingValues.value).join(', ');
  }

  function addMassUploadFiles(files: IMassUploadFile[]) {
    massUploadFiles.value = massUploadFiles.value.concat(files);
  }

  const missingFilesModalRef = ref<InstanceType<
    typeof MissingFilesModal
  > | null>(null);

  function checkMissingFilesAndUpload() {
    if (areRecordsWithoutFilePresent()) {
      skippedFiles.value = getFilePathsOfRecordsWithMissingFiles();
      /** If there are records without files, show the modal listing them */
      missingFilesModalRef.value?.show();
    } else {
      /** If there are no records without files, proceed with the upload */
      isUploadConfirmed.value = true;
      upload();
    }
  }

  async function handleIgnoreMissingFiles() {
    isUploadConfirmed.value = true;
    removeInvalidRecords();
    upload();
  }

  function checkIfUploadRecordsExist(): boolean {
    if (!uploadRecords.value.length) {
      createErrorToast(t('documentUpload.errors.noUploadRecords'));
      uploadFramesRerenderKey.value++;
      return false;
    }
    return true;
  }

  async function upload() {
    if (!checkIfUploadRecordsExist()) return;

    if (isUploadConfirmed.value) {
      /** to disable upload button */
      isUploading.value = true;

      try {
        await uploadFilesAndMetadata();
        createSuccessToast(t('documentUpload.uploadSuccessfull'));
        goToProjectDetails();
      } catch {
        isUploadFailed.value = true;
        fileUploadStatuses.value = {};
        createErrorToast(t('documentUpload.errors.uploadFailed'));
      } finally {
        isUploadFinished.value = true;
      }
    }
  }

  function isDigitalizationProjectMode(): boolean {
    return props.mode === ProjectType.DIGITALIZATION_PROJECT;
  }

  function goToProjectDetails() {
    goToProjectDetailsTab(
      props.mode,
      props.projectId,
      ProjectDetailsTab.UPLOADS,
      DigitalizationProjectDetailsTab.UPLOADS,
    );
  }

  onMounted(() => {
    getContentTypeMapping();
  });
</script>
<style scoped lang="scss">
  .mass-upload-container {
    padding-top: 2rem;
    .top-panel {
      margin-bottom: 3.125rem;
      h3 {
        font-weight: 700;
        font-size: 36px;
      }
    }
    .zip-tool-hint {
      margin: 1rem 0;
    }
    main {
      width: 100%;
      display: flex;
      flex-direction: column;
      button {
        align-self: flex-end;
        margin-top: 2rem;
      }
    }
  }
</style>
